import React, { useState, useRef, useEffect } from 'react';
import { Send, Copy, Check, Paperclip, X as XIcon, Loader2, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useQuickStartAssistant, QuickStartMessage } from '../../contexts/QuickStartAssistantContext';
import { copyFormattedContent } from '../../utils/copyFormatted';
import { useTheme } from '../../contexts/ThemeContext';
import { useScreenContext } from '../../hooks/useScreenContext';
import ActionCard from './ActionCard';

interface ChatMessageProps {
  message: QuickStartMessage;
  isPending: boolean;
  variant: 'bubble' | 'modal';
  onNavigated?: () => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, isPending, variant, onNavigated }) => {
  const messageRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const { isDarkMode } = useTheme();

  const handleCopy = async () => {
    if (messageRef.current && message.sender === 'ai') {
      const success = await copyFormattedContent(message.content, messageRef.current);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  return (
    <div className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[85%] ${message.sender === 'user' ? 'order-2' : 'order-1'}`}>
        <div
          ref={messageRef}
          className={`rounded-lg px-4 py-2 ${
            message.sender === 'user'
              ? 'bg-blue-500 text-white'
              : isDarkMode
                ? 'bg-gray-700 text-gray-100'
                : 'bg-gray-100 text-gray-900'
          }`}
        >
          <div className="message-content-inner">
            {message.sender === 'ai' ? (
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                  {message.content}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="whitespace-pre-wrap">{message.content}</p>
            )}
          </div>

          {message.attachments && message.attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {message.attachments.map((a) => (
                <span
                  key={a.localId}
                  title={a.name}
                  className="flex items-center gap-1 text-xs bg-white/20 rounded px-2 py-0.5 truncate max-w-[10rem]"
                >
                  <Paperclip className="w-3 h-3 flex-shrink-0" />
                  {a.name}
                </span>
              ))}
            </div>
          )}

          {message.isStreaming && !message.activity && (
            <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse" />
          )}

          {message.isStreaming && message.activity && (
            <p className={`text-xs italic mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {message.activity}
            </p>
          )}
        </div>

        {message.card && (
          <ActionCard card={message.card} isPending={isPending} variant={variant} onNavigated={onNavigated} />
        )}

        {message.sender === 'ai' && !message.isStreaming && (
          <button
            onClick={handleCopy}
            className={`mt-1 flex items-center gap-1 text-xs ${
              isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
            } transition-colors`}
          >
            {copied ? (
              <>
                <Check className="w-3 h-3" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                <span>Copy</span>
              </>
            )}
          </button>
        )}

        <div className={`text-xs mt-1 ${message.sender === 'user' ? 'text-right text-gray-500' : 'text-gray-500'}`}>
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
};

// Deterministic, no AI call — context-aware chips when a folder is in view
// (the backend's screen-context sentence resolves "this folder"/"this
// account" itself), a generic set everywhere else.
// "Anything need my attention?" leads every context (Phase 4) — it's a
// pull-based, account-wide scan (get_attention_items), not scoped to
// whatever folder/customer happens to be in view, so it belongs first
// regardless of screen context.
function suggestionChips(projectId?: string, customerId?: string): string[] {
  if (projectId) {
    return ['Anything need my attention?', "What's in this folder?", 'What should I run on this folder?', 'Anything missing before renewal?'];
  }
  if (customerId) {
    return ['Anything need my attention?', 'What do we have on file for this account?', 'What should I run next?', 'Explain a coverage question'];
  }
  return ['Anything need my attention?', 'Find a customer', 'What did we find in our last report?', 'Explain a coverage question'];
}

interface AssistantConversationProps {
  variant?: 'bubble' | 'modal';
  autoFocus?: boolean;
  /** Fires after a card action navigates the user away — hosts typically close themselves so the destination page is visible. */
  onNavigated?: () => void;
}

/**
 * Arie's conversation body — messages, the one live action card, and the
 * input row. This is the tool-calling operations copilot (search → propose
 * an action card → confirm), hosted by BOTH the Quick Start modal and the
 * floating chat bubble (see SupportChatWindow) — one assistant, one thread
 * (QuickStartAssistantContext), two entry points.
 */
const TEXTAREA_MIN_ROWS = 1;
const TEXTAREA_MAX_ROWS = 5;

const AssistantConversation: React.FC<AssistantConversationProps> = ({ variant = 'bubble', autoFocus = true, onNavigated }) => {
  const { messages, pendingCard, sendMessage, intakeAttachments, attachFilesToIntake, removeIntakeAttachment, knowledgeMode, setKnowledgeMode } = useQuickStartAssistant();
  const navigate = useNavigate();
  const isAgencyMode = knowledgeMode === 'agency';
  const { isDarkMode } = useTheme();
  const screenContext = useScreenContext();

  const [inputMessage, setInputMessage] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Auto-grow 1→5 rows as the user types, then scroll internally past that.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight || '20');
    const maxHeight = lineHeight * TEXTAREA_MAX_ROWS;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [inputMessage]);

  const send = async (text: string) => {
    if (!text.trim() && intakeAttachments.length === 0) return;
    setInputMessage('');
    await sendMessage(text.trim() || 'Set these up and tell me what to run.');
  };

  const handleSend = () => send(inputMessage);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFilesChosen = (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length > 0) attachFilesToIntake(list);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFilesChosen(e.dataTransfer.files);
  };

  const wrap = variant === 'modal' ? 'max-w-2xl mx-auto' : '';

  return (
    <div
      className="flex flex-col h-full relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragOver && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary rounded-lg pointer-events-none">
          <p className="text-sm font-medium text-primary bg-white dark:bg-gray-800 px-3 py-1.5 rounded-full shadow">
            Drop files to attach
          </p>
        </div>
      )}
      {/* Messages */}
      <div className={`flex-1 overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`${wrap} p-4`}>
          {messages.length === 0 ? (
            <div className="text-center mt-8">
              <p className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Hi, I'm Arie.</p>
              <p className="text-sm mt-2 text-gray-500 max-w-sm mx-auto">
                Your insurance expert and operator — ask me anything, or tell me what to get done. I'll find the customer, folder, and documents myself.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-5">
                {suggestionChips(screenContext.projectId, screenContext.customerId).map((chip) => (
                  <button
                    key={chip}
                    onClick={() => send(chip)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      isDarkMode
                        ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  isPending={!!pendingCard && message.card === pendingCard}
                  variant={variant}
                  onNavigated={onNavigated}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      {/* Input area */}
      <div className={`border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        {intakeAttachments.length > 0 && (
          <div className={`${wrap} flex flex-wrap gap-1.5 px-3 pt-3`}>
            {intakeAttachments.map((a) => (
              <span
                key={a.localId}
                title={a.errorMessage || a.name}
                className={`flex items-center gap-1 text-xs rounded-full px-2 py-1 truncate max-w-[10rem] ${
                  a.status === 'error'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    : isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {a.status === 'uploading' ? (
                  <Loader2 className="w-3 h-3 flex-shrink-0 animate-spin" />
                ) : (
                  <Paperclip className="w-3 h-3 flex-shrink-0" />
                )}
                <span className="truncate">{a.name}</span>
                <button onClick={() => removeIntakeAttachment(a.localId)} className="flex-shrink-0 hover:text-red-500">
                  <XIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        {/* General / Agency Knowledge toggle — Agency grounds every turn on the company Knowledge Base */}
        <div className={`${wrap} flex items-center px-3 pt-3`}>
          <div
            role="radiogroup"
            aria-label="Answer source"
            className={`inline-flex rounded-full p-0.5 text-xs ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
          >
            {([
              { mode: 'general' as const, label: 'General' },
              { mode: 'agency' as const, label: 'Agency Knowledge' },
            ]).map(({ mode, label }) => {
              const active = knowledgeMode === mode;
              return (
                <button
                  key={mode}
                  role="radio"
                  aria-checked={active}
                  onClick={() => setKnowledgeMode(mode)}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full transition-colors ${
                    active
                      ? 'bg-blue-500 text-white shadow-sm'
                      : isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {mode === 'agency' && <BookOpen className="w-3.5 h-3.5" />}
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        <div className={`${wrap} flex items-end gap-2 p-3`}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) handleFilesChosen(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            title="Attach documents"
            className={`p-2 rounded-lg shrink-0 ${
              isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            } transition-colors`}
          >
            <Paperclip className="w-5 h-5" />
          </button>

          <textarea
            ref={inputRef}
            rows={TEXTAREA_MIN_ROWS}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={isAgencyMode ? 'Ask your company knowledge base…' : "Type what you'd like to do... (Shift+Enter for a new line)"}
            className={`flex-1 resize-none px-3 py-2 rounded-lg ${
              isDarkMode ? 'bg-gray-700 text-gray-100 placeholder-gray-400 focus:bg-gray-600' : 'bg-gray-100 text-gray-900 placeholder-gray-500 focus:bg-white'
            } focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors`}
          />

          <button
            onClick={handleSend}
            disabled={!inputMessage.trim() && intakeAttachments.length === 0}
            className={`p-2 rounded-lg shrink-0 ${
              inputMessage.trim() || intakeAttachments.length > 0
                ? 'bg-blue-500 hover:bg-blue-600 text-white'
                : isDarkMode
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            } transition-colors`}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        {isAgencyMode && (
          <div className={`${wrap} px-3 pb-2 -mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Answers come only from documents in the{' '}
            <button
              onClick={() => {
                navigate('/knowledge-base');
                onNavigated?.();
              }}
              className="text-blue-500 hover:underline"
            >
              Knowledge Base
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantConversation;
